import { ReactNode } from 'react';

import { Button, ButtonProps, Dialog, DialogProps, Text12 } from '@sternum/ui';

import { ConfirmationDialogStyled, ConfirmModalContent, ConfirmModalDescription, Title } from './ConfirmModal.styles';

export interface ConfirmModalProps extends Omit<DialogProps, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  confirmLabel?: ReactNode;
  confirmButtonVariant?: ButtonProps['variant'];
  cancelLabel?: ReactNode;
  onCancel?: () => unknown;
  onConfirm?: () => unknown;
  confirmIcon?: ReactNode;
  cancelIcon?: ReactNode;
  maxWidth?: number;
  isCentered?: boolean;
  showCancelButton?: boolean;
  showConfirmButton?: boolean;
  cancelButtonDisabled?: boolean;
  confirmButtonDisabled?: boolean;
}

export function ConfirmModal({
  title = 'Confirm',
  description = 'Are you sure?',
  confirmLabel = 'Confirm',
  confirmButtonVariant = 'primary',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  confirmIcon,
  cancelIcon,
  isVisible,
  maxWidth,
  onCloseDialog,
  showCancelButton = true,
  showConfirmButton = true,
  confirmButtonDisabled = false,
  cancelButtonDisabled = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  theme, // partial DialogTheme cannot be used with styled-component theme
  isCentered,
  ...rest
}: ConfirmModalProps) {
  return (
    <ConfirmationDialogStyled
      isVisible={isVisible}
      role="dialog"
      aria-label="confirm"
      closeOnClickOutside={false}
      {...rest}
      onCloseDialog={(e) => onCloseDialog?.(e)}
      aria-hidden={!isVisible}
    >
      <ConfirmModalContent $textAlign={isCentered ? 'center' : 'left'} $maxWidth={maxWidth}>
        <Title>{title}</Title>
        <ConfirmModalDescription>
          <Text12>{description}</Text12>
        </ConfirmModalDescription>
      </ConfirmModalContent>
      <Dialog.Footer alignItems={isCentered ? 'center' : 'right'} isTopBorderVisible={false}>
        {showCancelButton && (
          <Button
            size="small"
            leftIcon={cancelIcon}
            variant="tertiary"
            aria-label="cancel"
            disabled={cancelButtonDisabled}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              onCancel?.();
              onCloseDialog?.(e);
            }}
          >
            {cancelLabel}
          </Button>
        )}

        {showConfirmButton && (
          <Button
            size="small"
            leftIcon={confirmIcon}
            variant={confirmButtonVariant}
            aria-label="confirm"
            disabled={confirmButtonDisabled}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              onConfirm?.();
              onCloseDialog?.(e);
            }}
          >
            {confirmLabel}
          </Button>
        )}
      </Dialog.Footer>
    </ConfirmationDialogStyled>
  );
}
